import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/ui/Icon";

type Tab = "url" | "upload";

const TABS: Tab[] = ["upload", "url"];

interface ImageDialogProps {
  open: boolean;
  onClose: () => void;
  onInsert: (src: string, alt?: string, file?: File) => void;
}

export function ImageDialog({ open, onClose, onInsert }: ImageDialogProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("upload");
  const [url, setUrl] = useState("");
  const [alt, setAlt] = useState("");
  const [preview, setPreview] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = () => {
    setUrl("");
    setAlt("");
    setPreview(null);
    setFileName(null);
    setSelectedFile(null);
    setTab("upload");
    setDragging(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onClose();
      setTimeout(resetState, 200);
    }
  };

  const handleInsert = () => {
    const src = tab === "url" ? url.trim() : preview;
    if (!src) return;
    onInsert(src, alt.trim() || undefined, tab === "upload" ? selectedFile ?? undefined : undefined);
    onClose();
    setTimeout(resetState, 200);
  };

  const processFile = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) return;
    setFileName(file.name);
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const canInsert = tab === "url" ? url.trim().length > 0 : preview !== null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        {/* Header */}
        <DialogHeader>
          <DialogTitle>
            <Icon name="image" size={14} />
            {t("imageDialog.title")}
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-[2px] px-3 py-2 border-b border-border-light bg-notelist">
          {TABS.map((tabKey) => (
            <Button
              key={tabKey}
              variant={tab === tabKey ? "primary" : "ghost"}
              size="sm"
              onClick={() => setTab(tabKey)}
            >
              {tabKey === "url" ? <Icon name="link" size={13} /> : <Icon name="drive" size={13} />}
              {tabKey === "url" ? t("imageDialog.tabUrl") : t("imageDialog.tabUpload")}
            </Button>
          ))}
        </div>

        {/* Body */}
        <div className="p-4">
          {tab === "url" ? (
            <div className="flex flex-col gap-3">
              <Input
                label={t("imageDialog.urlLabel")}
                type="url"
                placeholder={t("imageDialog.urlPlaceholder")}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && canInsert && handleInsert()
                }
                autoFocus
              />
              <Input
                label={t("imageDialog.altLabel")}
                type="text"
                placeholder={t("imageDialog.altPlaceholder")}
                value={alt}
                onChange={(e) => setAlt(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && canInsert && handleInsert()
                }
              />
              {url && (
                <div className="rounded-[7px] overflow-hidden bg-border-light min-h-[60px]">
                  <img
                    src={url}
                    alt={alt || t("imageDialog.previewAlt")}
                    className="w-full max-h-[160px] object-cover block"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                    onLoad={(e) => {
                      (e.target as HTMLImageElement).style.display = "block";
                    }}
                    style={{ display: "none" }}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div
                className={[
                  "relative border-[1.5px] border-dashed rounded-[9px] transition-[border-color,background] duration-150 overflow-hidden min-h-[130px] flex items-center justify-center",
                  preview
                    ? "border-solid border-accent cursor-default"
                    : "cursor-pointer",
                  !preview && dragging ? "border-accent bg-accent-bg" : "",
                  !preview && !dragging
                    ? "border-border hover:border-accent hover:bg-accent-bg"
                    : "",
                ].join(" ")}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                onClick={() => !preview && fileInputRef.current?.click()}
              >
                {preview ? (
                  <>
                    <img
                      src={preview}
                      alt={t("imageDialog.previewAlt")}
                      className="w-full max-h-[180px] object-cover block"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-transparent hover:bg-overlay transition-[background] duration-150">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="opacity-0 hover:opacity-100 focus-visible:opacity-100 transition-opacity duration-150"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreview(null);
                          setFileName(null);
                          fileInputRef.current?.click();
                        }}
                      >
                        {t("imageDialog.changeImage")}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-[0.4rem] p-6 pointer-events-none">
                    <div
                      className={`w-10 h-10 rounded-[10px] flex items-center justify-center mb-1 transition-[background,color] duration-150 ${dragging ? "bg-accent-bg text-accent" : "bg-border-light text-muted"}`}
                    >
                      <Icon name="drive" size={20} />
                    </div>
                    <p className="text-[13px] font-medium text-ink-secondary m-0">
                      {dragging
                        ? t("imageDialog.dropToInsert")
                        : t("imageDialog.dropOrClick")}
                    </p>
                    <p className="text-[11px] text-muted m-0">
                      {t("imageDialog.formats")}
                    </p>
                  </div>
                )}
              </div>
              {fileName && (
                <p className="flex items-center gap-[0.35rem] text-xs text-accent m-0">
                  <Icon name="check" size={12} />
                  {fileName}
                </p>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={handleFileChange}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => handleOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button size="sm" disabled={!canInsert} onClick={handleInsert}>
            {t("imageDialog.insert")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
